import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Btn } from "../components/common";
import { overlayCloseHandlers } from "../lib/overlayClose";
import { braceIssues, BracesWarning, fmtDT } from "./crm/crmui";
import { ArrowLeft, Save, Send, Trash2, Copy, GripVertical, Type, Image as ImageIcon,
  MousePointerClick, Minus, Rows3, BarChart3, Quote, Timer, PanelLeft, TestTube2, Eye, Mail, Monitor, Smartphone,
  Palette, Layout, Upload, Library, RefreshCw, AlertTriangle, X, ListChecks } from "lucide-react";

const BLOCK_META = {
  heading:     { icon: Type,              label: "Titulo" },
  paragraph:   { icon: Type,              label: "Paragrafo" },
  image:       { icon: ImageIcon,         label: "Imagem" },
  button:      { icon: MousePointerClick, label: "Botao (CTA)" },
  divider:     { icon: Minus,             label: "Divisor" },
  spacer:      { icon: Rows3,             label: "Espacamento" },
  two_cols:    { icon: PanelLeft,         label: "2 Colunas" },
  kpi_row:     { icon: BarChart3,         label: "KPIs" },
  testimonial: { icon: Quote,             label: "Testemunho" },
  countdown:   { icon: Timer,             label: "Contador (countdown)" },
  section:     { icon: Layout,            label: "Seccao (com fundo)" },
  footer:      { icon: Palette,           label: "Rodape inline" },
};

const DEFAULTS = {
  heading:     { text: "Novo titulo", level: 1, align: "left" },
  paragraph:   { text: "Escreva aqui o texto do paragrafo.", align: "left" },
  image:       { src: "", alt: "", href: "", width: "600" },
  button:      { label: "Saber mais", href: "https://cogniseg.com", color: "#007acc", align: "center" },
  divider:     {},
  spacer:      { height: 24 },
  two_cols:    { left: { heading: "Coluna 1", text: "Texto..." }, right: { heading: "Coluna 2", text: "Texto..." } },
  kpi_row:     { kpis: [{ value: "100+", label: "Clientes" }, { value: "24/7", label: "Suporte" }, { value: "99%", label: "SLA" }] },
  testimonial: { quote: "Reduzimos custos em 30%.", author: "Nome do cliente", role: "Cargo" },
  countdown:   { target_date: "2026-12-31", text_before: "Oferta termina em", text_after: "" },
  section:     { background_color: "#0B2545", background_image: "", text_color: "#ffffff", padding: "32px 28px", align: "left",
                 heading: "Titulo da seccao", heading_level: 2, text: "Subtitulo com destaque colorido.",
                 button_label: "Saber mais", button_href: "https://cogniseg.com", button_color: "#007acc" },
  footer:      { text: "Cogniseg - Security & Compliance\nRua Exemplo, 12\n1000-001 Lisboa", html: "", background_color: "#f1f5f9", text_color: "#64748b", align: "center" },
};

const uid = () => "b_" + Math.random().toString(36).slice(2, 10);

export default function CampaignEditor() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const fromTemplateId = sp.get("template");
  const nav = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [selBlockIdx, setSelBlockIdx] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewMode, setPreviewMode] = useState("desktop"); // desktop | mobile
  const [heatmap, setHeatmap] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAb, setShowAb] = useState(false);
  const [recipientsPreview, setRecipientsPreview] = useState(null);
  const [lists, setLists] = useState([]);
  const [segments, setSegments] = useState([]);
  const [tagsAll, setTagsAll] = useState([]);
  const [campaignStats, setCampaignStats] = useState(null);
  const [senders, setSenders] = useState([]);
  const [showVarHelper, setShowVarHelper] = useState(false);
  const [availableVars, setAvailableVars] = useState([]);
  const [previewContactId, setPreviewContactId] = useState("");
  const [previewContacts, setPreviewContacts] = useState([]);
  const [lastFocusedInput, setLastFocusedInput] = useState(null);
  const [showFooter, setShowFooter] = useState(false);
  const [showSends, setShowSends] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaFor, setMediaFor] = useState(null); // {blockIdx, field} onde inserir o URL
  const [mediaList, setMediaList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sends, setSends] = useState([]);
  const [sendsFilter, setSendsFilter] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (id && id !== "new") {
          const { data } = await api.get(`/marketing/campaigns/${id}`);
          setCampaign(data);
          try { const { data: hm } = await api.get(`/marketing/campaigns/${id}/heatmap`); setHeatmap(hm); } catch { /* silent */ }
          try { const { data: st } = await api.get(`/marketing/campaigns/${id}/stats`); setCampaignStats(st); } catch { /* silent */ }
        } else if (fromTemplateId) {
          const { data } = await api.post(`/marketing/campaigns/from-template/${fromTemplateId}`, { plataforma: "" });
          nav(`/app/marketing/editor/${data.id}`, { replace: true });
        } else {
          setCampaign({
            nome: "Nova campanha", assunto: "", plataforma: "", blocks: [],
            list_ids: [], segment_ids: [], tags_include: [], tags_exclude: [],
            track_opens: true, track_clicks: true,
            ab_test_active: false, ab_assunto_b: "", ab_ratio_a: 50,
            footer_custom: { enabled: false, text_html: "", bg_color: "", text_color: "", align: "center" },
            estado: "rascunho",
          });
        }
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    })();
    // Metadata para dropdowns
    api.get("/marketing/lists").then(({ data }) => setLists(data || [])).catch(() => {});
    api.get("/marketing/segments").then(({ data }) => setSegments(data || [])).catch(() => {});
    api.get("/marketing/tags").then(({ data }) => setTagsAll(data || [])).catch(() => {});
    api.get("/marketing/senders").then(({ data }) => setSenders((data || []).filter((s) => s.ativo !== false && (s.finalidade || "marketing") === "marketing"))).catch(() => {});
    api.get("/marketing/campaigns-vars").then(({ data }) => setAvailableVars(data?.vars || [])).catch(() => {});
  }, [id, fromTemplateId]); // eslint-disable-line

  // Preview de destinatarios (debounced 500ms)
  useEffect(() => {
    if (!campaign?.id) { setRecipientsPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(`/marketing/campaigns/${campaign.id}/preview`);
        setRecipientsPreview(data);
      } catch { /* silent */ }
    }, 600);
    return () => clearTimeout(t);
  }, [campaign?.id, campaign?.list_ids, campaign?.segment_ids, campaign?.tags_include, campaign?.tags_exclude]);

  // Preview live (debounced 500ms)
  useEffect(() => {
    if (!campaign) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/marketing/campaigns/preview-blocks", {
          blocks: campaign.blocks || [], plataforma: campaign.plataforma,
          contact_id: previewContactId || "",
          footer_custom: campaign.footer_custom || {},
        });
        setPreviewHtml(data.html);
      } catch { /* silent */ }
    }, 500);
    return () => clearTimeout(t);
  }, [campaign?.blocks, campaign?.plataforma, previewContactId, campaign?.footer_custom]);

  // Carrega alguns contactos para seleccao no preview (usa marketing_contacts)
  useEffect(() => {
    api.get("/marketing/contacts?limit=100").then(({ data }) => {
      const arr = Array.isArray(data) ? data : (data?.contacts || []);
      setPreviewContacts(arr.filter((c) => c.email).slice(0, 200));
    }).catch(() => setPreviewContacts([]));
  }, []);

  // Callback para inputs guardarem o cursor ao ganharem foco
  const rememberFocus = (e) => setLastFocusedInput(e.target);
  // Escuta global: memoriza o ultimo input/textarea com foco para permitir
  // inserir variaveis a partir da paleta com um so click.
  useEffect(() => {
    const onFocusIn = (e) => {
      const el = e.target;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) {
        setLastFocusedInput(el);
      }
    };
    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, []);
  const insertVarAtCursor = async (key) => {
    const tok = `{${key}}`;
    const el = lastFocusedInput;
    if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA") && typeof el.selectionStart === "number") {
      const start = el.selectionStart, end = el.selectionEnd;
      const val = el.value;
      const next = val.slice(0, start) + tok + val.slice(end);
      // Utiliza o Native Setter para forcar React a detectar a mudanca
      const setter = Object.getOwnPropertyDescriptor(el.tagName === "INPUT" ? window.HTMLInputElement.prototype : window.HTMLTextAreaElement.prototype, "value").set;
      setter.call(el, next);
      el.dispatchEvent(new Event("input", { bubbles: true }));
      requestAnimationFrame(() => { try { el.focus(); el.setSelectionRange(start + tok.length, start + tok.length); } catch (_) {} });
      return;
    }
    // Fallback: copia para clipboard
    try { await navigator.clipboard.writeText(tok); toast.success(`${tok} copiado.`); }
    catch { toast.message(tok); }
  };

  // @dnd-kit sensors (deve estar antes de qualquer early return)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (!campaign) return <div className="p-8 text-muted2">A carregar...</div>;

  const addBlock = (type) => {
    const b = { id: uid(), type, props: { ...DEFAULTS[type] } };
    setCampaign({ ...campaign, blocks: [...(campaign.blocks || []), b] });
    setSelBlockIdx((campaign.blocks || []).length);
  };
  const moveBlock = (idx, delta) => {
    const arr = [...campaign.blocks];
    const target = idx + delta;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setCampaign({ ...campaign, blocks: arr });
    setSelBlockIdx(target);
  };
  const dupBlock = (idx) => {
    const arr = [...campaign.blocks];
    const copy = { ...arr[idx], id: uid() };
    arr.splice(idx + 1, 0, copy);
    setCampaign({ ...campaign, blocks: arr });
  };
  const delBlock = (idx) => {
    const arr = [...campaign.blocks]; arr.splice(idx, 1);
    setCampaign({ ...campaign, blocks: arr });
    setSelBlockIdx(null);
  };
  const updateBlockProps = (idx, patch) => {
    const arr = [...campaign.blocks];
    arr[idx] = { ...arr[idx], props: { ...(arr[idx].props || {}), ...patch } };
    setCampaign({ ...campaign, blocks: arr });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        nome: campaign.nome, assunto: campaign.assunto, plataforma: campaign.plataforma || "",
        servico: campaign.servico || "", corpo_html: "", template_id: "",
        blocks: campaign.blocks || [],
        list_ids: campaign.list_ids || [], segment_ids: campaign.segment_ids || [],
        tags_include: campaign.tags_include || [], tags_exclude: campaign.tags_exclude || [],
        sender_id: campaign.sender_id || "", sender_email: campaign.sender_email || "",
        sender_name: campaign.sender_name || "", reply_to: campaign.reply_to || "",
        agendado_em: campaign.agendado_em || "",
        track_opens: !!campaign.track_opens, track_clicks: !!campaign.track_clicks,
        partner_code: campaign.partner_code || "",
        ab_test_active: !!campaign.ab_test_active,
        ab_assunto_b: campaign.ab_assunto_b || "",
        ab_ratio_a: Number(campaign.ab_ratio_a || 50),
        footer_custom: campaign.footer_custom || {},
      };
      if (campaign.id) {
        await api.put(`/marketing/campaigns/${campaign.id}`, payload);
      } else {
        const { data } = await api.post("/marketing/campaigns", payload);
        nav(`/app/marketing/editor/${data.id}`, { replace: true });
      }
      toast.success("Campanha guardada.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const loadSends = async (estado = "") => {
    if (!campaign?.id) return;
    try {
      const url = `/marketing/campaigns/${campaign.id}/sends${estado ? `?estado=${estado}` : ""}`;
      const { data } = await api.get(url);
      setSends(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openSends = async () => {
    setShowSends(true);
    await loadSends(sendsFilter);
  };

  const resendCampaign = async (onlyFailed = true) => {
    if (!campaign?.id) return;
    if (!window.confirm(onlyFailed
      ? "Reenviar apenas aos que falharam? Os registos existentes serao actualizados."
      : "Reenviar a TODOS os destinatarios (mesmo aos que ja receberam)?"
    )) return;
    setResending(true);
    try {
      const { data } = await api.post(`/marketing/campaigns/${campaign.id}/resend`, { only_failed: onlyFailed });
      toast.success(`Reenvio: ${data.enviados} OK / ${data.erros} erros (${data.total} tentativas).`);
      await loadSends(sendsFilter);
      try { const { data: st } = await api.get(`/marketing/campaigns/${campaign.id}/stats`); setCampaignStats(st); } catch { /* silent */ }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setResending(false); }
  };

  const loadMedia = async () => {
    try {
      const { data } = await api.get("/marketing/media");
      setMediaList(data || []);
    } catch { /* silent */ }
  };

  const openMediaPicker = (blockIdx, field = "src") => {
    setMediaFor({ blockIdx, field });
    setShowMediaPicker(true);
    loadMedia();
  };

  const publicBaseUrl = () => {
    // Preferir a origin do backend configurado (funciona para preview e producao)
    const raw = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return String(raw).replace(/\/+$/, "");
  };

  const mediaUrl = (m) => m?.url_publico || `${publicBaseUrl()}/api/marketing/m/${m.id}`;

  const pickMedia = (m) => {
    if (!mediaFor) return;
    const url = mediaUrl(m);
    const idx = mediaFor.blockIdx;
    const field = mediaFor.field || "src";
    updateBlockProps(idx, { [field]: url });
    setShowMediaPicker(false);
    setMediaFor(null);
    toast.success("Imagem inserida.");
  };

  const uploadMedia = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) { toast.error(`'${file.name}' excede 5MB.`); continue; }
        const b64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result).split(",")[1] || "");
          r.onerror = rej;
          r.readAsDataURL(file);
        });
        const { data } = await api.post("/marketing/media", {
          nome: file.name, mime: file.type || "image/png", content_b64: b64, tags: [],
        });
        setMediaList((prev) => [data, ...prev]);
        toast.success(`Upload OK: ${file.name}`);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha no upload."); }
    finally { setUploading(false); }
  };

  const deleteMedia = async (mid) => {
    if (!window.confirm("Eliminar esta imagem da biblioteca?")) return;
    try {
      await api.delete(`/marketing/media/${mid}`);
      setMediaList((prev) => prev.filter((m) => m.id !== mid));
      toast.success("Imagem eliminada.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendTest = async () => {
    if (!campaign.id) { toast.message("Guarde primeiro a campanha."); return; }
    const email = window.prompt("Enviar email de teste para:", "");
    if (email === null) return;
    try {
      const { data } = await api.post(`/marketing/campaigns/${campaign.id}/send-test`, { email: email || "" });
      toast.success(`Teste enviado para ${data.para}.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao enviar teste."); }
  };

  // @dnd-kit
  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = campaign.blocks.findIndex((b) => b.id === active.id);
    const newIndex = campaign.blocks.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const arr = arrayMove(campaign.blocks, oldIndex, newIndex);
    setCampaign({ ...campaign, blocks: arr });
    setSelBlockIdx(newIndex);
  };

  const sel = selBlockIdx != null ? campaign.blocks[selBlockIdx] : null;

  return (
    <>
      <PageHeader overline="Marketing / Campanhas"
        title={campaign.id ? campaign.nome : "Nova campanha"}
        action={
          <div className="flex gap-2 flex-wrap">
            <Btn variant="ghost" onClick={() => nav("/app/marketing")}><ArrowLeft size={14} /> Voltar</Btn>
            <Btn variant="ghost" onClick={() => setShowFooter((v) => !v)} testid="campaign-toggle-footer">
              <Palette size={14} /> Rodape {campaign.footer_custom?.enabled ? "ON" : "off"}
            </Btn>
            <Btn variant="ghost" onClick={() => setShowAb((v) => !v)} testid="campaign-toggle-ab">
              <TestTube2 size={14} /> A/B {campaign.ab_test_active ? "ON" : "off"}
            </Btn>
            <Btn variant="ghost" onClick={sendTest} testid="campaign-send-test">
              <Mail size={14} /> Enviar teste
            </Btn>
            {campaign.id && (
              <Btn variant="ghost" onClick={openSends} testid="campaign-view-sends">
                <ListChecks size={14} /> Envios
              </Btn>
            )}
            <Btn onClick={save} disabled={saving} testid="campaign-save"><Save size={14} /> {saving ? "A guardar..." : "Guardar"}</Btn>
          </div>
        }
      />

      {/* Barra de metadados */}
      <div className="grid md:grid-cols-4 gap-2 mb-3">
        <div>
          <label className="text-[10px] font-semibold text-muted2 uppercase">Nome</label>
          <input value={campaign.nome} onChange={(e) => setCampaign({ ...campaign, nome: e.target.value })}
            data-testid="campaign-nome" className="w-full text-sm border border-tint rounded px-2 py-1.5" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-muted2 uppercase">Assunto</label>
          <input value={campaign.assunto} onChange={(e) => setCampaign({ ...campaign, assunto: e.target.value })}
            data-testid="campaign-assunto" className="w-full text-sm border border-tint rounded px-2 py-1.5" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-muted2 uppercase">Plataforma alvo</label>
          <select value={campaign.plataforma || ""} onChange={(e) => setCampaign({ ...campaign, plataforma: e.target.value })}
            data-testid="campaign-plataforma"
            className="w-full text-sm border border-tint rounded px-2 py-1.5 bg-white">
            <option value="">-- Sem restricao (generica) --</option>
            {Array.from(new Set(senders.map((s) => s.plataforma).filter(Boolean))).sort().map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
            {campaign.plataforma && !senders.some((s) => s.plataforma === campaign.plataforma) && (
              <option value={campaign.plataforma}>{campaign.plataforma} (livre)</option>
            )}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-muted2 uppercase">Remetente</label>
          <select value={campaign.sender_id || ""} onChange={(e) => {
            const sid = e.target.value;
            const s = senders.find((x) => x.id === sid);
            setCampaign({
              ...campaign,
              sender_id: sid,
              sender_email: s?.from_email || "",
              sender_name: s?.from_name || "",
              reply_to: s?.reply_to || campaign.reply_to || "",
            });
          }} data-testid="campaign-sender" className="w-full text-sm border border-tint rounded px-2 py-1.5 bg-white">
            <option value="">Auto (por plataforma / predefinido)</option>
            {senders.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome} {s.plataforma ? `[${s.plataforma}]` : ""} - {s.from_email}
                {s.predefinido ? " (predef)" : ""}
              </option>
            ))}
          </select>
        </div>
      </div>
      {senders.length === 0 && (
        <div className="text-[11px] rounded p-2 bg-amber-50 border border-amber-200 text-amber-900 mb-3" data-testid="no-senders-warning">
          Sem remetentes de marketing configurados. Vai a <b>Marketing -> Remetentes</b> e cria pelo menos um (com finalidade = marketing) para poderes enviar campanhas.
        </div>
      )}

      {/* Segmentacao + preview destinatarios (Iter99c) */}
      <div className="border border-tint rounded-md bg-white p-3 mb-3" data-testid="campaign-segmentation">
        <div className="grid md:grid-cols-4 gap-2 items-start">
          <div>
            <label className="text-[10px] font-semibold text-muted2 uppercase">Listas ({(campaign.list_ids || []).length})</label>
            <div className="mt-1 max-h-24 overflow-y-auto border border-tint rounded p-1 text-[12px]">
              {lists.length === 0 && <div className="text-muted2 italic px-1">Sem listas.</div>}
              {lists.map((l) => (
                <label key={l.id} className="flex items-center gap-1.5 px-1 py-0.5 hover:bg-slate-50">
                  <input type="checkbox" checked={(campaign.list_ids || []).includes(l.id)} data-testid={`seg-list-${l.id}`}
                    onChange={(e) => setCampaign({ ...campaign, list_ids: e.target.checked ? [...(campaign.list_ids || []), l.id] : (campaign.list_ids || []).filter((x) => x !== l.id) })} />
                  <span className="truncate">{l.nome} <span className="text-muted2">({l.n_contactos || 0})</span></span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted2 uppercase">Segmentos ({(campaign.segment_ids || []).length})</label>
            <div className="mt-1 max-h-24 overflow-y-auto border border-tint rounded p-1 text-[12px]">
              {segments.length === 0 && <div className="text-muted2 italic px-1">Sem segmentos.</div>}
              {segments.map((s) => (
                <label key={s.id} className="flex items-center gap-1.5 px-1 py-0.5 hover:bg-slate-50">
                  <input type="checkbox" checked={(campaign.segment_ids || []).includes(s.id)} data-testid={`seg-segment-${s.id}`}
                    onChange={(e) => setCampaign({ ...campaign, segment_ids: e.target.checked ? [...(campaign.segment_ids || []), s.id] : (campaign.segment_ids || []).filter((x) => x !== s.id) })} />
                  <span className="truncate">{s.nome}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted2 uppercase">Tags incluir</label>
            <input value={(campaign.tags_include || []).join(",")} onChange={(e) => setCampaign({ ...campaign, tags_include: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
              data-testid="seg-tags-include" placeholder="tag1,tag2"
              className="w-full text-[12px] border border-tint rounded px-2 py-1 mt-1" />
            <label className="text-[10px] font-semibold text-muted2 uppercase mt-2 block">Tags excluir</label>
            <input value={(campaign.tags_exclude || []).join(",")} onChange={(e) => setCampaign({ ...campaign, tags_exclude: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
              data-testid="seg-tags-exclude" placeholder="opt-out"
              className="w-full text-[12px] border border-tint rounded px-2 py-1 mt-1" />
          </div>
          <div className="border-l border-tint pl-2">
            <label className="text-[10px] font-semibold text-muted2 uppercase">Destinatarios (preview)</label>
            {!recipientsPreview ? (
              <div className="text-[12px] text-muted2 italic mt-1">Guarde a campanha para ver o preview.</div>
            ) : (
              <>
                <div className="text-2xl font-bold text-navy leading-none mt-1" data-testid="preview-n">{recipientsPreview.n_destinatarios}</div>
                <div className="text-[10px] text-muted2 mb-1">contactos elegiveis</div>
                <div className="border-t border-tint pt-1 mt-1 max-h-20 overflow-y-auto" data-testid="preview-sample">
                  {(recipientsPreview.sample || []).map((s, i) => (
                    <div key={i} className="text-[11px] text-slate-600 truncate">{s.nome || s.email}<span className="text-muted2"> - {s.email}</span></div>
                  ))}
                  {(recipientsPreview.sample || []).length === 0 && <div className="text-[11px] text-muted2 italic">Sem contactos.</div>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* A/B Analytics (Iter99c) - apenas para campanhas com envios A/B */}
      {campaignStats?.ab && (campaignStats.ab.A || campaignStats.ab.B) && (
        <div className="border border-blue/40 bg-blue/5 rounded-md p-3 mb-3" data-testid="campaign-ab-analytics">
          <div className="flex items-center gap-2 mb-2">
            <TestTube2 size={14} className="text-blue" />
            <span className="text-[11px] font-semibold text-navy uppercase">A/B analytics</span>
            {(() => {
              const a = campaignStats.ab.A?.open_rate || 0;
              const b = campaignStats.ab.B?.open_rate || 0;
              const winner = a > b ? "A" : b > a ? "B" : "empate";
              return <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${winner === 'empate' ? 'bg-slate-200 text-slate-700' : 'bg-emerald-100 text-emerald-800'}`}>Vencedor abertura: {winner}</span>;
            })()}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {["A", "B"].map((v) => {
              const s = campaignStats.ab[v] || { sends: 0, opens: 0, clicks: 0, open_rate: 0, click_rate: 0 };
              return (
                <div key={v} className="border border-tint rounded bg-white p-2" data-testid={`ab-variant-${v}`}>
                  <div className="text-[10px] font-bold text-navy uppercase mb-1">Variante {v}</div>
                  <div className="text-[11px] text-muted2 truncate italic">{v === "A" ? campaign.assunto : campaign.ab_assunto_b || "(igual a A)"}</div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div><div className="text-[9px] uppercase text-slate-500">Envios</div><div className="text-lg font-bold text-navy">{s.sends}</div></div>
                    <div><div className="text-[9px] uppercase text-slate-500">Abertura</div><div className="text-lg font-bold text-blue">{s.open_rate}%</div></div>
                    <div><div className="text-[9px] uppercase text-slate-500">Click</div><div className="text-lg font-bold text-emerald-600">{s.click_rate}%</div></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}


      {showFooter && (
        <div className="border border-emerald-300 bg-emerald-50/60 rounded-md p-3 mb-3" data-testid="campaign-footer-panel">
          <label className="flex items-center gap-2 text-sm text-navy">
            <input type="checkbox" checked={!!campaign.footer_custom?.enabled}
              onChange={(e) => setCampaign({ ...campaign, footer_custom: { ...(campaign.footer_custom || {}), enabled: e.target.checked } })}
              data-testid="footer-enabled" />
            <Palette size={14} /> Rodape personalizado (override do rodape padrao)
          </label>
          {campaign.footer_custom?.enabled && (
            <div className="mt-3 space-y-2">
              <div className="grid md:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] font-semibold text-muted2 uppercase">Cor fundo</label>
                  <div className="flex items-center gap-1 mt-1">
                    <input type="color" value={campaign.footer_custom?.bg_color || "#f1f5f9"}
                      onChange={(e) => setCampaign({ ...campaign, footer_custom: { ...(campaign.footer_custom || {}), bg_color: e.target.value } })}
                      data-testid="footer-bg" className="w-10 h-8 border border-tint rounded" />
                    <input value={campaign.footer_custom?.bg_color || ""}
                      onChange={(e) => setCampaign({ ...campaign, footer_custom: { ...(campaign.footer_custom || {}), bg_color: e.target.value } })}
                      placeholder="ex: transparent"
                      className="flex-1 text-[12px] border border-tint rounded px-2 py-1" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-muted2 uppercase">Cor texto</label>
                  <div className="flex items-center gap-1 mt-1">
                    <input type="color" value={campaign.footer_custom?.text_color || "#64748b"}
                      onChange={(e) => setCampaign({ ...campaign, footer_custom: { ...(campaign.footer_custom || {}), text_color: e.target.value } })}
                      data-testid="footer-fg" className="w-10 h-8 border border-tint rounded" />
                    <input value={campaign.footer_custom?.text_color || ""}
                      onChange={(e) => setCampaign({ ...campaign, footer_custom: { ...(campaign.footer_custom || {}), text_color: e.target.value } })}
                      placeholder="#64748b"
                      className="flex-1 text-[12px] border border-tint rounded px-2 py-1" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-muted2 uppercase">Alinhar</label>
                  <select value={campaign.footer_custom?.align || "center"}
                    onChange={(e) => setCampaign({ ...campaign, footer_custom: { ...(campaign.footer_custom || {}), align: e.target.value } })}
                    data-testid="footer-align"
                    className="w-full text-[12px] border border-tint rounded px-2 py-1 mt-1 bg-white">
                    <option value="left">Esquerda</option><option value="center">Centro</option><option value="right">Direita</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-muted2 uppercase">HTML (mostrado como esta; suporta variaveis)</label>
                <textarea rows={4} value={campaign.footer_custom?.text_html || ""}
                  onChange={(e) => setCampaign({ ...campaign, footer_custom: { ...(campaign.footer_custom || {}), text_html: e.target.value } })}
                  data-testid="footer-html"
                  placeholder="<b>Cogniseg</b><br/>Rua Exemplo, 123 - Lisboa<br/>www.cogniseg.com | 253 00 89 89"
                  className="w-full text-[12px] border border-tint rounded px-2 py-1 mt-1 font-mono" />
              </div>
              <p className="text-[10px] text-muted2">O link "Deixar de receber estes emails" e adicionado automaticamente. Suporta {"{empresa}"}, {"{nome}"}, etc.</p>
            </div>
          )}
        </div>
      )}

      {showAb && (
        <div className="border border-blue/40 bg-blue/5 rounded-md p-3 mb-3" data-testid="campaign-ab-panel">
          <label className="flex items-center gap-2 text-sm text-navy">
            <input type="checkbox" checked={!!campaign.ab_test_active}
              onChange={(e) => setCampaign({ ...campaign, ab_test_active: e.target.checked })}
              data-testid="campaign-ab-active" />
            <TestTube2 size={14} /> Activar A/B testing (dois assuntos, split aleatorio)
          </label>
          {campaign.ab_test_active && (
            <div className="grid md:grid-cols-2 gap-2 mt-2">
              <div>
                <label className="text-[10px] font-semibold text-muted2 uppercase">Assunto B (alternativo)</label>
                <input value={campaign.ab_assunto_b || ""} onChange={(e) => setCampaign({ ...campaign, ab_assunto_b: e.target.value })}
                  data-testid="campaign-ab-assunto-b" className="w-full text-sm border border-tint rounded px-2 py-1.5" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-muted2 uppercase">% para variante A ({campaign.ab_ratio_a || 50}% A / {100 - (campaign.ab_ratio_a || 50)}% B)</label>
                <input type="range" min="10" max="90" value={campaign.ab_ratio_a || 50}
                  onChange={(e) => setCampaign({ ...campaign, ab_ratio_a: Number(e.target.value) })}
                  data-testid="campaign-ab-ratio" className="w-full" />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-12 gap-3">
        {/* Paleta de blocos */}
        <aside className="col-span-3 border border-tint rounded-lg bg-white p-3" data-testid="palette">
          <div className="text-[11px] font-semibold text-navy uppercase mb-2">Adicionar bloco</div>
          <div className="space-y-1">
            {Object.entries(BLOCK_META).map(([k, m]) => {
              const Ic = m.icon;
              return (
                <button key={k} onClick={() => addBlock(k)} data-testid={`palette-${k}`}
                  className="w-full flex items-center gap-2 text-left text-[13px] text-navy px-2 py-1.5 rounded hover:bg-blue/10">
                  <Ic size={14} className="text-blue" /> {m.label}
                </button>
              );
            })}
          </div>

          {/* Variaveis do contacto: click insere {key} no campo em edicao */}
          <div className="mt-4 pt-3 border-t border-tint" data-testid="campaign-vars-helper">
            <button type="button" onClick={() => setShowVarHelper((v) => !v)}
              className="w-full text-left text-[11px] font-semibold text-navy uppercase mb-2 hover:text-blue flex items-center gap-1">
              <Type size={12} /> Variaveis do contacto {showVarHelper ? "▲" : "▼"}
            </button>
            {showVarHelper && (
              <>
                <p className="text-[10px] text-muted2 leading-relaxed mb-2">
                  Clique num chip para <b>inserir no campo em edicao</b> (assunto, titulos, paragrafos, botao). No envio, cada {"{"}variavel{"}"} e substituida pelos dados do contacto.
                </p>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {availableVars.map((v) => (
                    <button key={v.key} type="button"
                      onClick={() => insertVarAtCursor(v.key)}
                      data-testid={`campaign-var-${v.key}`}
                      className="w-full flex flex-col items-start px-2 py-1 rounded hover:bg-blue/10 text-left">
                      <code className="text-[11px] text-blue font-mono">{`{${v.key}}`}</code>
                      <span className="text-[10px] text-muted2">{v.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </aside>

        {/* Canvas */}
        <main className="col-span-6 border border-tint rounded-lg bg-slate-50 p-3 min-h-[400px]" data-testid="canvas">
          <div className="text-[11px] font-semibold text-navy uppercase mb-2">Estrutura ({(campaign.blocks || []).length} blocos) - arrastar para reordenar</div>
          {(campaign.blocks || []).length === 0 && (
            <div className="text-sm text-muted2 italic p-6 text-center border border-dashed border-tint rounded">Adicione blocos pela paleta a esquerda.</div>
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={(campaign.blocks || []).map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {(campaign.blocks || []).map((b, i) => (
                  <SortableBlock key={b.id} block={b} index={i} isSel={selBlockIdx === i}
                    onSelect={() => setSelBlockIdx(i)}
                    onDup={() => dupBlock(i)}
                    onDel={() => delBlock(i)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <p className="text-[10px] text-muted2 mt-3">Variaveis disponiveis: {"{nome}"}, {"{email}"}, {"{empresa}"}, {"{plataforma}"}, {"{hoje}"}.</p>
        </main>

        {/* Props do bloco selecionado */}
        <aside className="col-span-3 border border-tint rounded-lg bg-white p-3" data-testid="props-panel">
          <div className="text-[11px] font-semibold text-navy uppercase mb-2">Editar bloco</div>
          {sel ? (
            <div>
              <div className="text-[12px] font-semibold text-blue mb-2">{BLOCK_META[sel.type]?.label}</div>
              <BlockPropsForm block={sel} onChange={(p) => updateBlockProps(selBlockIdx, p)}
                onPickMedia={(field) => openMediaPicker(selBlockIdx, field)}
                knownVars={availableVars.map((v) => v.key)} />
            </div>
          ) : (
            <div className="text-[12px] text-muted2 italic">Selecione um bloco no canvas para editar as propriedades.</div>
          )}
        </aside>
      </div>

      {/* Preview full-width com dimensao maior */}
      <div className="mt-4 border border-tint rounded-lg bg-white p-3" data-testid="preview-panel">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div className="text-[11px] font-semibold text-navy uppercase flex items-center gap-1"><Eye size={12} /> Preview do email</div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-1 text-[11px] text-muted2">
              Contacto:
              <select value={previewContactId} onChange={(e) => setPreviewContactId(e.target.value)}
                data-testid="preview-contact-picker"
                className="text-[11px] border border-tint rounded px-1.5 py-0.5 bg-white max-w-[220px]">
                <option value="">-- Ana Silva (sample) --</option>
                {previewContacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome || c.email}{c.empresa ? ` - ${c.empresa}` : ""}
                  </option>
                ))}
              </select>
            </label>
            <div className="inline-flex bg-slate-100 rounded-md p-0.5" data-testid="preview-mode-toggle">
            <button type="button" onClick={() => setPreviewMode("desktop")}
              data-testid="preview-mode-desktop"
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded transition ${previewMode === "desktop" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}>
              <Monitor size={12} /> Desktop (900px)
            </button>
            <button type="button" onClick={() => setPreviewMode("mobile")}
              data-testid="preview-mode-mobile"
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded transition ${previewMode === "mobile" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}>
              <Smartphone size={12} /> Mobile (375px)
            </button>
          </div>
          </div>
        </div>
        <div className="bg-slate-50 rounded p-4 flex justify-center" style={{ minHeight: 640 }}>
          <div className="border border-tint rounded overflow-hidden bg-white transition-all duration-200 shadow-sm"
            style={{ width: previewMode === "mobile" ? 375 : "100%", maxWidth: previewMode === "mobile" ? 375 : 900, height: 640 }}>
            <iframe title="preview" srcDoc={previewHtml} className="w-full h-full border-0 bg-white" data-testid="campaign-preview-iframe" />
          </div>
        </div>
      </div>

      {/* Heatmap de cliques */}
      {heatmap && (heatmap.urls || []).length > 0 && (
        <div className="mt-4 border border-tint rounded-lg bg-white p-3" data-testid="campaign-heatmap">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-semibold text-navy uppercase">Heatmap de cliques ({heatmap.total_clicks} clicks totais)</div>
          </div>
          <table className="w-full text-[12.5px]">
            <thead className="text-[10px] uppercase text-slate-500 border-b border-tint">
              <tr><th className="text-left py-1">URL</th><th className="text-right py-1 w-24">Clicks</th><th className="text-right py-1 w-24">Unicos</th><th className="w-32"></th></tr>
            </thead>
            <tbody>
              {heatmap.urls.map((u, i) => {
                const pct = heatmap.total_clicks ? Math.round((u.clicks / heatmap.total_clicks) * 100) : 0;
                return (
                  <tr key={i} className="border-b border-tint last:border-b-0">
                    <td className="py-1 truncate max-w-md"><a href={u.url} target="_blank" rel="noreferrer" className="text-blue hover:underline">{u.url}</a></td>
                    <td className="text-right font-semibold text-navy">{u.clicks}</td>
                    <td className="text-right text-muted2">{u.unique}</td>
                    <td>
                      <div className="w-full h-1.5 bg-slate-200 rounded overflow-hidden">
                        <div className="h-full bg-blue" style={{ width: `${pct}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal: biblioteca de imagens (picker + upload) */}
      {showMediaPicker && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="media-picker-modal"
          {...overlayCloseHandlers(() => { setShowMediaPicker(false); setMediaFor(null); })}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-tint">
              <div className="flex items-center gap-2">
                <Library size={16} className="text-blue" />
                <div className="font-semibold text-navy">Biblioteca de imagens</div>
                <span className="text-[11px] text-muted2">({mediaList.length})</span>
              </div>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1 text-[12px] font-semibold text-white bg-blue hover:bg-blue/80 rounded px-3 py-1.5 cursor-pointer" data-testid="media-upload-btn">
                  <Upload size={13} /> {uploading ? "A enviar..." : "Carregar imagem"}
                  <input type="file" multiple accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                    className="hidden" data-testid="media-upload-input"
                    onChange={(e) => uploadMedia(e.target.files)} />
                </label>
                <button onClick={() => { setShowMediaPicker(false); setMediaFor(null); }} className="text-muted2 hover:text-red-500" data-testid="media-close">
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {mediaList.length === 0 ? (
                <div className="text-center text-muted2 italic py-8">Sem imagens. Carregue a primeira acima (max 5MB).</div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  {mediaList.map((m) => (
                    <div key={m.id} className="border border-tint rounded overflow-hidden bg-slate-50" data-testid={`media-item-${m.id}`}>
                      <div className="aspect-square bg-white flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80"
                        onClick={() => pickMedia(m)}>
                        <img src={mediaUrl(m)} alt={m.nome} className="max-w-full max-h-full object-contain"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      </div>
                      <div className="p-1.5 flex items-center justify-between gap-1 border-t border-tint">
                        <div className="text-[10px] text-navy truncate flex-1" title={m.nome}>{m.nome}</div>
                        <button onClick={() => deleteMedia(m.id)} className="text-red-500 hover:text-red-700" data-testid={`media-del-${m.id}`}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-4 py-2 border-t border-tint bg-slate-50 text-[10px] text-muted2">
              Formatos aceites: PNG, JPG, GIF, WebP, SVG. As imagens ficam publicas em <code>/api/marketing/m/&#123;id&#125;</code>.
            </div>
          </div>
        </div>
      )}

      {/* Modal: envios detalhados (com resend) */}
      {showSends && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="sends-modal"
          {...overlayCloseHandlers(() => setShowSends(false))}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-tint">
              <div className="flex items-center gap-2">
                <ListChecks size={16} className="text-blue" />
                <div className="font-semibold text-navy">Envios da campanha</div>
                <span className="text-[11px] text-muted2">({sends.length} registos)</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={sendsFilter} onChange={(e) => { setSendsFilter(e.target.value); loadSends(e.target.value); }}
                  data-testid="sends-filter"
                  className="text-[12px] border border-tint rounded px-2 py-1 bg-white">
                  <option value="">Todos os estados</option>
                  <option value="enviado">Enviado</option>
                  <option value="erro">Erro</option>
                  <option value="bounced">Bounced</option>
                </select>
                <button onClick={() => resendCampaign(true)} disabled={resending}
                  data-testid="resend-failed-btn"
                  className="inline-flex items-center gap-1 text-[12px] font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded px-3 py-1.5">
                  <RefreshCw size={12} /> {resending ? "A reenviar..." : "Reenviar aos que falharam"}
                </button>
                <button onClick={() => resendCampaign(false)} disabled={resending}
                  data-testid="resend-all-btn"
                  className="inline-flex items-center gap-1 text-[12px] font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded px-3 py-1.5">
                  <RefreshCw size={12} /> Reenviar a todos
                </button>
                <button onClick={() => setShowSends(false)} className="text-muted2 hover:text-red-500" data-testid="sends-close">
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {sends.length === 0 ? (
                <div className="text-center text-muted2 italic py-8">
                  {sendsFilter ? `Sem envios com estado '${sendsFilter}'.` : "Ainda nao ha envios para esta campanha."}
                </div>
              ) : (
                <table className="w-full text-[12.5px]" data-testid="sends-table">
                  <thead className="text-[10px] uppercase text-slate-500 border-b border-tint sticky top-0 bg-white">
                    <tr>
                      <th className="text-left py-1">Email</th>
                      <th className="text-left py-1 w-24">Estado</th>
                      <th className="text-left py-1 w-32">Enviado em</th>
                      <th className="text-left py-1">Erro</th>
                      <th className="text-center py-1 w-16">Abriu</th>
                      <th className="text-center py-1 w-16">Clicou</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sends.map((s) => (
                      <tr key={s.id} className="border-b border-tint last:border-b-0" data-testid={`send-row-${s.id}`}>
                        <td className="py-1 truncate max-w-xs">{s.email}</td>
                        <td className="py-1">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                            s.estado === "enviado" ? "bg-emerald-100 text-emerald-800" :
                            s.estado === "erro" ? "bg-red-100 text-red-800" :
                            s.estado === "bounced" ? "bg-amber-100 text-amber-800" :
                            "bg-slate-200 text-slate-700"
                          }`}>{s.estado || "?"}</span>
                        </td>
                        <td className="py-1 text-muted2 text-[11px]">{fmtDT(s.sent_at)}</td>
                        <td className="py-1 text-red-600 text-[11px] truncate max-w-md" title={s.erro || ""}>
                          {s.erro ? <><AlertTriangle size={10} className="inline" /> {s.erro}</> : ""}
                        </td>
                        <td className="py-1 text-center">{(s.opened_at || []).length > 0 ? <span className="text-emerald-600">OK</span> : ""}</td>
                        <td className="py-1 text-center">{(s.clicked_at || []).length > 0 ? <span className="text-blue">OK</span> : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SortableBlock({ block, index, isSel, onSelect, onDup, onDel }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const Ic = BLOCK_META[block.type]?.icon || Type;
  return (
    <div ref={setNodeRef} style={style} onClick={onSelect} data-testid={`block-${index}`}
      className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer ${isSel ? "border-blue bg-blue/5" : "border-tint bg-white hover:border-blue/50"}`}>
      <span {...attributes} {...listeners} data-testid={`block-drag-${index}`}
        className="text-muted2 hover:text-navy cursor-grab active:cursor-grabbing" onClick={(e) => e.stopPropagation()}>
        <GripVertical size={14} />
      </span>
      <Ic size={13} className="text-blue" />
      <span className="text-[12.5px] text-navy flex-1 truncate">
        {BLOCK_META[block.type]?.label || block.type}
        {block.props?.text && <span className="text-muted2"> - {(block.props.text || "").slice(0, 40)}</span>}
        {block.props?.label && <span className="text-muted2"> - {block.props.label}</span>}
      </span>
      <button onClick={(e) => { e.stopPropagation(); onDup(); }} data-testid={`block-dup-${index}`} className="text-muted2 hover:text-navy"><Copy size={13} /></button>
      <button onClick={(e) => { e.stopPropagation(); onDel(); }} data-testid={`block-del-${index}`} className="text-red-500 hover:text-red-700"><Trash2 size={13} /></button>
    </div>
  );
}

function BlockPropsForm({ block, onChange, onPickMedia, knownVars = [] }) {
  const p = block.props || {};
  const t = block.type;
  const inp = (k, ph = "", type = "text") => (
    <>
      <input value={p[k] || ""} onChange={(e) => onChange({ [k]: e.target.value })} placeholder={ph} type={type}
        data-testid={`prop-${t}-${k}`}
        className="w-full text-[12px] border border-tint rounded px-2 py-1 mt-0.5" />
      <BracesWarning issue={braceIssues(p[k], knownVars, "single")} testid={`prop-warn-${t}-${k}`} />
    </>
  );
  const txt = (k, ph = "", rows = 3) => (
    <>
      <textarea value={p[k] || ""} onChange={(e) => onChange({ [k]: e.target.value })} placeholder={ph} rows={rows}
        data-testid={`prop-${t}-${k}`}
        className="w-full text-[12px] border border-tint rounded px-2 py-1 mt-0.5" />
      <BracesWarning issue={braceIssues(p[k], knownVars, "single")} testid={`prop-warn-${t}-${k}`} />
    </>
  );
  const lbl = (s) => <label className="text-[10px] font-semibold text-slate-600 uppercase">{s}</label>;

  if (t === "heading") return (<div className="space-y-2">
    <div>{lbl("Texto")} {inp("text")}</div>
    <div className="grid grid-cols-2 gap-2">
      <div>{lbl("Nivel")}<select value={p.level || 1} onChange={(e) => onChange({ level: Number(e.target.value) })} className="w-full text-[12px] border border-tint rounded px-2 py-1 mt-0.5 bg-white"><option value={1}>H1</option><option value={2}>H2</option><option value={3}>H3</option></select></div>
      <div>{lbl("Alinhar")}<select value={p.align || "left"} onChange={(e) => onChange({ align: e.target.value })} className="w-full text-[12px] border border-tint rounded px-2 py-1 mt-0.5 bg-white"><option value="left">Esquerda</option><option value="center">Centro</option><option value="right">Direita</option></select></div>
    </div>
  </div>);
  if (t === "paragraph") return (<div className="space-y-2">{lbl("Texto")}{txt("text", "", 4)}
    {lbl("Alinhar")}<select value={p.align || "left"} onChange={(e) => onChange({ align: e.target.value })} className="w-full text-[12px] border border-tint rounded px-2 py-1 mt-0.5 bg-white"><option value="left">Esquerda</option><option value="center">Centro</option></select>
  </div>);
  if (t === "image") return (<div className="space-y-2">
    {lbl("URL da imagem")}
    <div className="flex items-center gap-1">
      <input value={p.src || ""} onChange={(e) => onChange({ src: e.target.value })} placeholder="https://..."
        data-testid={`prop-${t}-src`}
        className="flex-1 text-[12px] border border-tint rounded px-2 py-1" />
      <button type="button" onClick={() => onPickMedia && onPickMedia("src")}
        data-testid={`prop-${t}-pick`}
        className="text-[11px] font-semibold text-blue border border-blue rounded px-2 py-1 hover:bg-blue hover:text-white transition whitespace-nowrap">
        <Library size={11} className="inline" /> Biblioteca
      </button>
    </div>
    {p.src && (
      <div className="border border-tint rounded p-1 bg-slate-50" data-testid={`prop-${t}-thumb`}>
        <img src={p.src} alt="" className="max-h-24 mx-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      </div>
    )}
    {lbl("Alt")}{inp("alt")}{lbl("Link (opcional)")}{inp("href")}{lbl("Largura (px)")}{inp("width")}
  </div>);
  if (t === "button") return (<div className="space-y-2">{lbl("Label")}{inp("label")}{lbl("URL")}{inp("href")}{lbl("Cor (#hex)")}{inp("color")}</div>);
  if (t === "spacer") return (<div>{lbl("Altura (px)")}{inp("height", "24", "number")}</div>);
  if (t === "divider") return <p className="text-[11px] text-muted2">Sem propriedades.</p>;
  if (t === "section") return (<div className="space-y-2">
    <div className="grid grid-cols-2 gap-2">
      <div>{lbl("Cor fundo")}
        <div className="flex items-center gap-1">
          <input type="color" value={p.background_color || "#0B2545"} onChange={(e) => onChange({ background_color: e.target.value })}
            data-testid={`prop-${t}-bgcolor`} className="w-10 h-8 border border-tint rounded" />
          <input value={p.background_color || ""} onChange={(e) => onChange({ background_color: e.target.value })}
            placeholder="#0B2545" className="flex-1 text-[12px] border border-tint rounded px-2 py-1" />
        </div>
      </div>
      <div>{lbl("Cor texto")}
        <div className="flex items-center gap-1">
          <input type="color" value={p.text_color || "#ffffff"} onChange={(e) => onChange({ text_color: e.target.value })}
            data-testid={`prop-${t}-textcolor`} className="w-10 h-8 border border-tint rounded" />
          <input value={p.text_color || ""} onChange={(e) => onChange({ text_color: e.target.value })}
            placeholder="#ffffff" className="flex-1 text-[12px] border border-tint rounded px-2 py-1" />
        </div>
      </div>
    </div>
    {lbl("Imagem de fundo (URL)")}
    <div className="flex items-center gap-1">
      <input value={p.background_image || ""} onChange={(e) => onChange({ background_image: e.target.value })} placeholder="https://..."
        data-testid={`prop-${t}-bgimg`}
        className="flex-1 text-[12px] border border-tint rounded px-2 py-1" />
      <button type="button" onClick={() => onPickMedia && onPickMedia("background_image")}
        data-testid={`prop-${t}-pickbg`}
        className="text-[11px] font-semibold text-blue border border-blue rounded px-2 py-1 hover:bg-blue hover:text-white transition whitespace-nowrap">
        <Library size={11} className="inline" />
      </button>
    </div>
    {lbl("Padding (CSS)")}{inp("padding", "32px 28px")}
    <div className="grid grid-cols-2 gap-2">
      <div>{lbl("Alinhar")}<select value={p.align || "left"} onChange={(e) => onChange({ align: e.target.value })} className="w-full text-[12px] border border-tint rounded px-2 py-1 mt-0.5 bg-white"><option value="left">Esquerda</option><option value="center">Centro</option><option value="right">Direita</option></select></div>
      <div>{lbl("Nivel titulo")}<select value={p.heading_level || 2} onChange={(e) => onChange({ heading_level: Number(e.target.value) })} className="w-full text-[12px] border border-tint rounded px-2 py-1 mt-0.5 bg-white"><option value={1}>H1</option><option value={2}>H2</option><option value={3}>H3</option></select></div>
    </div>
    {lbl("Titulo")}{inp("heading")}
    {lbl("Texto")}{txt("text", "", 3)}
    <div className="border-t border-tint pt-2 mt-2">
      {lbl("Botao (CTA opcional)")}
      {inp("button_label", "Ex: Saber mais")}
      {inp("button_href", "https://...")}
      <div className="grid grid-cols-2 gap-2">
        <div>{lbl("Cor botao")}
          <input type="color" value={p.button_color || "#007acc"} onChange={(e) => onChange({ button_color: e.target.value })}
            data-testid={`prop-${t}-btncolor`} className="w-10 h-8 border border-tint rounded" />
        </div>
        <div>{lbl("Alinhamento botao")}
          <select value={p.button_align || p.align || "left"} onChange={(e) => onChange({ button_align: e.target.value })}
            data-testid={`prop-${t}-btnalign`}
            className="w-full text-[12px] border border-tint rounded px-2 py-1 mt-0.5 bg-white">
            <option value="left">Esquerda</option><option value="center">Centro</option><option value="right">Direita</option>
          </select>
        </div>
      </div>
    </div>
  </div>);
  if (t === "footer") return (<div className="space-y-2">
    {lbl("Texto (uma linha por para; ou usar HTML abaixo)")}{txt("text", "Empresa\nMorada\nContactos", 3)}
    {lbl("HTML personalizado (override do texto acima)")}{txt("html", "<b>Cogniseg</b><br/>...", 3)}
    <div className="grid grid-cols-2 gap-2">
      <div>{lbl("Cor fundo")}
        <input type="color" value={p.background_color || "#f1f5f9"} onChange={(e) => onChange({ background_color: e.target.value })}
          data-testid={`prop-${t}-bgcolor`} className="w-10 h-8 border border-tint rounded" />
      </div>
      <div>{lbl("Cor texto")}
        <input type="color" value={p.text_color || "#64748b"} onChange={(e) => onChange({ text_color: e.target.value })}
          data-testid={`prop-${t}-textcolor`} className="w-10 h-8 border border-tint rounded" />
      </div>
    </div>
    {lbl("Alinhar")}<select value={p.align || "center"} onChange={(e) => onChange({ align: e.target.value })} className="w-full text-[12px] border border-tint rounded px-2 py-1 mt-0.5 bg-white"><option value="left">Esquerda</option><option value="center">Centro</option><option value="right">Direita</option></select>
  </div>);
  if (t === "two_cols") return (<div className="space-y-2">
    <div className="border border-tint rounded p-2"><div className="text-[10px] font-semibold text-slate-600 uppercase mb-1">Coluna 1</div>
      <input value={p.left?.heading || ""} onChange={(e) => onChange({ left: { ...(p.left||{}), heading: e.target.value } })} placeholder="Titulo" className="w-full text-[12px] border border-tint rounded px-2 py-1 mb-1" />
      <textarea rows={2} value={p.left?.text || ""} onChange={(e) => onChange({ left: { ...(p.left||{}), text: e.target.value } })} placeholder="Texto" className="w-full text-[12px] border border-tint rounded px-2 py-1" />
    </div>
    <div className="border border-tint rounded p-2"><div className="text-[10px] font-semibold text-slate-600 uppercase mb-1">Coluna 2</div>
      <input value={p.right?.heading || ""} onChange={(e) => onChange({ right: { ...(p.right||{}), heading: e.target.value } })} placeholder="Titulo" className="w-full text-[12px] border border-tint rounded px-2 py-1 mb-1" />
      <textarea rows={2} value={p.right?.text || ""} onChange={(e) => onChange({ right: { ...(p.right||{}), text: e.target.value } })} placeholder="Texto" className="w-full text-[12px] border border-tint rounded px-2 py-1" />
    </div>
  </div>);
  if (t === "kpi_row") return (<div className="space-y-2">
    {(p.kpis || []).map((k, i) => (
      <div key={i} className="grid grid-cols-2 gap-1 border border-tint rounded p-1.5">
        <input value={k.value} onChange={(e) => { const arr = [...(p.kpis||[])]; arr[i] = { ...arr[i], value: e.target.value }; onChange({ kpis: arr }); }} placeholder="Valor" className="text-[12px] border border-tint rounded px-2 py-1" />
        <input value={k.label} onChange={(e) => { const arr = [...(p.kpis||[])]; arr[i] = { ...arr[i], label: e.target.value }; onChange({ kpis: arr }); }} placeholder="Label" className="text-[12px] border border-tint rounded px-2 py-1" />
      </div>
    ))}
    <div className="flex gap-1">
      <button onClick={() => onChange({ kpis: [...(p.kpis||[]), { value: "", label: "" }] })} className="text-[11px] text-blue hover:underline">+ KPI</button>
      {(p.kpis||[]).length > 1 && <button onClick={() => onChange({ kpis: (p.kpis||[]).slice(0, -1) })} className="text-[11px] text-red-500 hover:underline">- ultimo</button>}
    </div>
  </div>);
  if (t === "testimonial") return (<div className="space-y-2">{lbl("Citacao")}{txt("quote", "", 3)}{lbl("Autor")}{inp("author")}{lbl("Cargo")}{inp("role")}</div>);
  if (t === "countdown") return (<div className="space-y-2">{lbl("Data alvo (YYYY-MM-DD ou ISO)")}{inp("target_date", "2026-12-31")}{lbl("Texto antes")}{inp("text_before")}{lbl("Texto depois")}{inp("text_after")}</div>);
  return <p className="text-[11px] text-muted2">Bloco {t} sem editor.</p>;
}
